import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, AlertTriangle, RotateCw, Clock, XCircle } from "lucide-react";
import type { GoogleSyncStatus } from "@axorial/shared";
import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";

export const Route = createFileRoute("/admin/google-calendar-sync")({
  component: AdminGoogleCalendarSyncPage,
});

interface SyncUserRow {
  userId: string;
  email: string;
  name: string;
  active: boolean;
  enabled: boolean | null;
  lastSyncAt: number | null;
  lastSyncStatus: GoogleSyncStatus | null;
  lastSyncError: string | null;
  lastSyncCount: number;
}

const STATUS_STYLE: Record<
  GoogleSyncStatus | "never",
  { icon: React.ReactNode; label: string; cls: string }
> = {
  ok: {
    icon: <CheckCircle2 className="size-4" />,
    label: "OK",
    cls: "badge-success",
  },
  expired_token: {
    icon: <AlertTriangle className="size-4" />,
    label: "Token expirado",
    cls: "badge-warning",
  },
  auth_error: {
    icon: <XCircle className="size-4" />,
    label: "Auth error",
    cls: "badge-error",
  },
  rate_limited: {
    icon: <Clock className="size-4" />,
    label: "Rate limited",
    cls: "badge-warning",
  },
  error: {
    icon: <XCircle className="size-4" />,
    label: "Error",
    cls: "badge-error",
  },
  never: {
    icon: <Clock className="size-4" />,
    label: "Nunca",
    cls: "badge-ghost",
  },
};

function relativeTime(unixSeconds: number | null): string {
  if (!unixSeconds) return "—";
  const diffSec = Math.floor(Date.now() / 1000) - unixSeconds;
  if (diffSec < 60) return "hace segundos";
  if (diffSec < 3600) return `hace ${Math.floor(diffSec / 60)} min`;
  if (diffSec < 86400) return `hace ${Math.floor(diffSec / 3600)} h`;
  return formatDate(new Date(unixSeconds * 1000).toISOString().slice(0, 10));
}

function AdminGoogleCalendarSyncPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<{ users: SyncUserRow[] }>({
    queryKey: ["admin", "google-calendar-sync"],
    queryFn: () => api.get("/admin/google-calendar-sync"),
  });

  const force = useMutation({
    mutationFn: (userId: string) => api.post(`/admin/google-calendar-sync/${userId}/force`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "google-calendar-sync"] }),
  });

  if (isLoading || !data) {
    return (
      <div className="grid place-items-center py-20">
        <span className="loading loading-spinner loading-lg text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Sync de Google Calendar"
        subtitle="Estado del sync per-usuario. Corre cada 10 min vía cron. Forzar sync envía un tick inmediato para ese usuario."
      />
      <div className="card-elevated overflow-x-auto">
        <table className="table table-sm">
          <thead>
            <tr>
              <th>Usuario</th>
              <th>Habilitado</th>
              <th>Último sync</th>
              <th>Estado</th>
              <th>Eventos</th>
              <th>Error</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.users.map((u) => {
              const statusKey: GoogleSyncStatus | "never" = u.lastSyncStatus ?? "never";
              const style = STATUS_STYLE[statusKey];
              return (
                <tr key={u.userId}>
                  <td>
                    <div className="font-medium">{u.name}</div>
                    <div className="text-xs text-base-content/55">{u.email}</div>
                  </td>
                  <td>
                    {u.enabled === false ? (
                      u.lastSyncStatus === "auth_error" ? (
                        <span
                          className="badge badge-warning badge-sm"
                          title="Auto-deshabilitado: la SA no puede impersonar esta cuenta (probablemente es un alias/grupo/buzón compartido, no un user real)."
                        >
                          Auto-OFF
                        </span>
                      ) : (
                        <span className="badge badge-ghost badge-sm">Apagado</span>
                      )
                    ) : (
                      <span className="badge badge-success badge-sm">Encendido</span>
                    )}
                  </td>
                  <td className="text-xs text-base-content/65">{relativeTime(u.lastSyncAt)}</td>
                  <td>
                    <span className={`badge badge-sm gap-1 ${style.cls}`}>
                      {style.icon}
                      {style.label}
                    </span>
                  </td>
                  <td className="text-xs">{u.lastSyncCount}</td>
                  <td
                    className="text-[11px] text-base-content/55 max-w-xs truncate"
                    title={u.lastSyncError ?? undefined}
                  >
                    {u.lastSyncError ?? "—"}
                  </td>
                  <td>
                    <button
                      type="button"
                      onClick={() => force.mutate(u.userId)}
                      disabled={force.isPending && force.variables === u.userId}
                      className="btn btn-ghost btn-xs"
                      title="Forzar sync ahora"
                    >
                      {force.isPending && force.variables === u.userId ? (
                        <span className="loading loading-spinner loading-xs" />
                      ) : (
                        <RotateCw className="size-3" />
                      )}
                      Forzar
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
